/**
 * JourneySaveHint — one-time coach mark on /journey that points the
 * newcomer from the in-page "Sync my journey" button up to the rail's
 * "Save my progress" chip, so they see the sync path lives on every
 * page (not just this one).
 *
 * Rendered only when the visitor is signed out AND hasn't dismissed
 * this hint before. The arrow is a fixed-position SVG anchored to both
 * DOM targets — recomputed on resize / scroll.
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "wua:journey:save-hint:dismissed";
// Selectors we anchor the arrow between.
const FROM_SEL = "[data-testid='journey-map-signin']";      // in-page CTA (start)
const TO_SEL   = "[data-testid='journey-auth-chip-signed-out']";  // rail chip (end)

function readDismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return true; }
}
function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
}

export default function JourneySaveHint() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [anchors, setAnchors] = useState(null);   // {from:{x,y}, to:{x,y}}
  const wrapRef = useRef(null);

  // 1) Decide whether to show at all. Signed-in users and previous
  //    dismissers get nothing.
  useEffect(() => {
    if (user) return;
    if (readDismissed()) return;
    // Small delay so the rail + page finish rendering before we measure.
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, [user]);

  // 2) Measure the two anchor elements. Recompute on resize / scroll so
  //    the arrow stays glued to the buttons as the layout reflows.
  useEffect(() => {
    if (!visible) return;
    const measure = () => {
      const from = document.querySelector(FROM_SEL);
      const to   = document.querySelector(TO_SEL);
      if (!from || !to) { setAnchors(null); return; }
      const f = from.getBoundingClientRect();
      const t = to.getBoundingClientRect();
      setAnchors({
        from: { x: f.left + f.width / 2, y: f.top + f.height / 2 },
        to:   { x: t.left + t.width / 2, y: t.bottom + 6 },
        fromRect: f,
        toRect:   t,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [visible]);

  // 3) Dismiss on Escape for keyboard users.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible]);

  const close = () => {
    markDismissed();
    setVisible(false);
  };

  if (!visible || user) return null;
  if (!anchors) return null;

  const { from, to } = anchors;
  // Build a smooth cubic-bezier curve from (from) up to (to). The
  // control points bow the curve outward for a nice arc; sign of the
  // horizontal offset makes it swing left or right depending on which
  // side the rail chip sits (usually right, but resilient either way).
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const swing = Math.sign(dx) || 1;
  const c1 = { x: from.x + swing * 60, y: from.y - Math.abs(dy) * 0.15 };
  const c2 = { x: to.x - swing * 40,   y: to.y   + Math.abs(dy) * 0.4  };
  const path = `M ${from.x} ${from.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${to.x} ${to.y}`;

  // Highlight ring around the rail chip so the arrow's endpoint feels
  // "landed" on a target.
  const ringR = Math.max(anchors.toRect.width, anchors.toRect.height) / 2 + 8;

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 z-[80] pointer-events-none"
      data-testid="journey-save-hint"
      aria-live="polite"
    >
      {/* Dimming veil — visual only. Click-through is enabled so users
          can interact with any /journey CTA on the very first click; the
          hint dismisses via the explicit "Got it" button or Escape. */}
      <div
        className="absolute inset-0 bg-slate-900/25 pointer-events-none"
        data-testid="journey-save-hint-veil"
        aria-hidden="true"
      />

      {/* Arrow + target ring */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        role="presentation"
      >
        <defs>
          <marker
            id="save-hint-arrow"
            viewBox="0 0 12 12"
            refX="6" refY="6"
            markerWidth="10" markerHeight="10"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L12 6 L0 12 Z" fill="#FBBF24" />
          </marker>
        </defs>

        {/* Target ring around the rail chip */}
        <circle
          cx={to.x} cy={to.y - 6} r={ringR}
          fill="none"
          stroke="#FBBF24"
          strokeWidth="2.5"
          strokeDasharray="4 4"
          opacity="0.9"
        >
          <animate attributeName="r" values={`${ringR - 2};${ringR + 4};${ringR - 2}`}
                   dur="1.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.9;0.4;0.9"
                   dur="1.8s" repeatCount="indefinite" />
        </circle>

        {/* Curved arrow itself */}
        <path
          d={path}
          stroke="#F59E0B"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
          markerEnd="url(#save-hint-arrow)"
          strokeDasharray="0.1 8"
        >
          <animate attributeName="stroke-dashoffset" values="16;0"
                   dur="0.9s" repeatCount="indefinite" />
        </path>
      </svg>

      {/* Tooltip bubble anchored near the FROM button */}
      <div
        className="absolute pointer-events-auto max-w-[280px] rounded-lg bg-white border border-amber-300 shadow-xl p-3.5"
        style={{
          left: Math.min(from.x + 24, window.innerWidth - 300),
          top:  from.y + 24,
        }}
        data-testid="journey-save-hint-tip"
        role="dialog"
        aria-label="Sync hint"
      >
        <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-1">
          The rail follows you
        </div>
        <p className="text-[13px] text-stone-800 leading-snug">
          <strong>Save my progress</strong> lives up here on every page —
          one sign-in syncs your journey across your phone, laptop,
          and any new device.
        </p>
        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            onClick={close}
            data-testid="journey-save-hint-dismiss"
            className="text-[11px] text-stone-500 hover:text-stone-800 underline underline-offset-2"
          >
            Got it
          </button>
          <span className="text-[10px] text-stone-400">Press Esc to close</span>
        </div>
      </div>
    </div>
  );
}
